import {
  Box,
  Button,
  Cards,
  Container,
  Grid,
  Pagination,
  PropertyFilter,
  TextFilter,
} from '@cloudscape-design/components';
import { Header } from '@cloudscape-design/components';
import { SpaceBetween } from '@cloudscape-design/components';
import { useMemo, useState } from 'react';
import { ReviewBasic } from '../common/types';
import { ReviewContent } from './ReviewContent';
import { formatDateString } from '../common/formatting/dateFormat';
import { ReviewText } from './ReviewText';
import { ChatBubble, Avatar } from '@cloudscape-design/chat-components';
import { PropertyFilterQuery, useCollection } from '@cloudscape-design/collection-hooks';

export type SmallReviewListProps = {
  reviews: ReviewBasic[];
  placeName: string;
  header?: React.ReactNode;
  cardsPerRow?: number;
  includeResponse?: boolean;
  truncateCharacters?: number;
  maxCards?: number;
};

export function SmallReviewList(props: SmallReviewListProps) {
  const { reviews, placeName, cardsPerRow, includeResponse, truncateCharacters, maxCards } = props;

  const initials = useMemo(() => {
    // if multiple words, use first letter of first two words
    if (placeName.includes(' ')) {
      return placeName
        .split(' ')
        .slice(0, 2)
        .map((word) => word[0])
        .join('');
    }
    // if one word but more than 2 capital letters, use first two capital letters
    let initials = '';
    for (const char of placeName) {
      if (char.toUpperCase() === char) {
        initials += char;
        if (initials.length === 2) {
          break;
        }
      }
    }
    if (initials.length === 0) {
      return placeName.slice(0, 2);
    }
  }, [placeName]);

  const [filteringText, setFilteringText] = useState('');
  const [textFilter, setTextFilter] = useState('');

  const [query, setQuery] = useState<PropertyFilterQuery>({
    tokens: [],
    operation: 'and',
  });

  const filteredReviews = useMemo(() => {
    let filtered = reviews.filter((review) => {
      let match = true;
      const textMatch =
        review.content?.toLowerCase().includes(textFilter.toLowerCase()) ||
        review.response_content?.toLowerCase().includes(textFilter.toLowerCase());

      match = match && !!textMatch;

      if (query.tokens.length > 0) {
        for (const token of query.tokens) {
          if (token.propertyKey === 'stars') {
            if (token.operator === '=') {
              match = match && review.stars === parseInt(token.value);
            } else if (token.operator === '!=') {
              match = match && review.stars !== parseInt(token.value);
            } else if (token.operator === '>=') {
              match = match && (review.stars ?? 0) >= parseInt(token.value);
            } else if (token.operator === '<=') {
              match = match && (review.stars ?? 0) <= parseInt(token.value);
            } else if (token.operator === '>') {
              match = match && (review.stars ?? 0) > parseInt(token.value);
            } else if (token.operator === '<') {
              match = match && (review.stars ?? 0) < parseInt(token.value);
            }
          } else if (token.propertyKey === 'response') {
            if (token.operator === '=') {
              if (token.value === 'true') {
                match = match && !!review.response_content;
              } else if (token.value === 'false') {
                match = match && !review.response_content;
              }
            }
          }
        }
      }

      return match;
    });

    // replace newlines with spaces
    filtered = filtered.map((review) => ({
      ...review,
      content: review.content?.replace(/\n/g, ' '),
    }));

    if (maxCards) {
      return filtered.slice(0, maxCards);
    }

    return filtered;
  }, [reviews, textFilter, query, maxCards]);

  return (
    <SpaceBetween direction="vertical" size="m">
      {props.header}
      <Grid
        gridDefinition={[
          {
            colspan: {
              default: 4,
            },
          },
          {
            colspan: {
              default: 4,
            },
          },
          {
            colspan: {
              default: 4,
            },
          },
          {
            colspan: {
              default: 4,
            },
          },
          {
            colspan: {
              default: 4,
            },
          },
          {
            colspan: {
              default: 4,
            },
          },
        ]}
      >
        {filteredReviews.map((review) => (
          <SpaceBetween direction="vertical" size="xxxs">
            <ChatBubble
              key={review.uuid + '-review'}
              ariaLabel="Review"
              type="incoming"
              avatar={
                <Avatar
                  key={review.uuid + '-review-avatar'}
                  ariaLabel={
                    review.review_date
                      ? formatDateString(review.review_date)
                      : (review.recency ?? '')
                  }
                  tooltipText={`Review from ${
                    review.review_date
                      ? formatDateString(review.review_date)
                      : (review.recency ?? '')
                  }`}
                />
              }
            >
              <ReviewContent
                key={review.uuid + '-review-content'}
                review_id={review.review_id}
                text={review.content}
                stars={review.stars}
                truncateCharacters={truncateCharacters}
                recency={review.review_date ? formatDateString(review.review_date) : review.recency}
                highlights={textFilter ? [textFilter] : undefined}
                highlightCaseSensitive={false}
              />
            </ChatBubble>
            {review.response_content ? (
              includeResponse ? (
                <ChatBubble
                  key={review.uuid + '-response'}
                  ariaLabel="Review Response"
                  type="outgoing"
                  avatar={
                    <Avatar
                      key={review.uuid + '-response-avatar'}
                      ariaLabel={placeName}
                      tooltipText={placeName}
                      initials={initials}
                      color="gen-ai"
                    />
                  }
                >
                  <ReviewContent
                    key={review.uuid + '-response-content'}
                    text={review.response_content}
                    recency={
                      review.response_date ? formatDateString(review.response_date) : undefined
                    }
                    highlights={textFilter ? [textFilter] : undefined}
                    highlightCaseSensitive={false}
                    truncateCharacters={truncateCharacters}
                  />
                </ChatBubble>
              ) : null
            ) : (
              <Box variant="p" textAlign="center" key={review.uuid + '-response-no-response'}>
                (No response)
              </Box>
            )}
          </SpaceBetween>
        ))}
      </Grid>
    </SpaceBetween>
  );
}
